import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import callrecorderApi from '../../api/callrecorderApi';
import callrecorderUrls from "../../configs/callrecorder/callrecorderUrls";
import { UserInterface } from '../../store/callrecorder/usersSlice';
import { BundleInterface } from '../../store/callrecorder/bundlesSlice';
import { UserNumberInterface } from '../../store/callrecorder/userNumbersSlice';
import { bundleEntity, bundlesEntity } from "./bundlesActions";
import { userNumberEntity, userNumbersEntity } from "./userNumbersActions";

const PREFIX = 'callrecorder';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: callrecorderUrls.userPath(value.id)
  };
}

export const usersEntity = new schema.Entity(
  'users',
  {
    bundle: bundlesEntity,
    user_number: userNumbersEntity
  },
  {
    processStrategy
  }
);

export const userEntity = new schema.Entity(
  'user',
  {
    bundle: bundleEntity,
    user_number: userNumberEntity
  },
  {
    processStrategy
  }
);

const fetchUsersCollection = async (url: string, params: object = {}) => {
  const response = await callrecorderApi.get(url, { params })
  const normalized = normalize<
    any,
    {
      bundles: { [key: string]: BundleInterface };
      userNumbers: { [key: string]: UserNumberInterface };
      users: { [key: string]: UserInterface };
    }
  >(response.data.users, [usersEntity]);

  return {
    bundles: normalized.entities?.bundles || {},
    userNumbers: normalized.entities?.userNumbers || {},
    users: normalized.entities?.users || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      bundle: { [key: string]: BundleInterface };
      userNumber: { [key: string]: UserNumberInterface };
      user: UserInterface;
    }
  >(response.data.user, userEntity);
  return normalized.entities;
}

export const fetchUsers = createAsyncThunk(
  `${PREFIX}_users/fetchUsers`,
  async (params: object = {}) => fetchUsersCollection('/onesky/users', params)
);

export const fetchUser = createAsyncThunk(
  `${PREFIX}_users/fetchUser`,
  async (id: number | string) => {
    const response = await callrecorderApi.get(`/onesky/users/${id}`);
    return normalizeInstance(response);
  }
);

export const updateUser = createAsyncThunk(
  `${PREFIX}_users/updateUser`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await callrecorderApi.patch(`/onesky/users/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
